export type TailSide = 'top' | 'right' | 'bottom' | 'left'

export interface SpeechBubblePathInput {
  width: number
  height: number
  radii: { tl: number; tr: number; br: number; bl: number }
  borderWidth: number
  tail: {
    show: boolean
    side: TailSide
    offsetPercent: number
    width: number
    height: number
    tipRadius: number
  }
}

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n))

const fmt = (n: number) => (Number.isFinite(n) ? +n.toFixed(2) : 0)

export function buildSpeechBubblePath(input: SpeechBubblePathInput): string {
  const { width, height, borderWidth, tail } = input
  if (width <= 0 || height <= 0) return ''

  const sw = Math.max(0, borderWidth)
  const half = sw / 2

  // Path coordinates inset so stroke (centered on path) sits flush with the body's outer box.
  const left = half
  const top = half
  const right = width - half
  const bottom = height - half
  const innerW = Math.max(0, right - left)
  const innerH = Math.max(0, bottom - top)
  const maxRadius = Math.min(innerW, innerH) / 2

  const tl = clamp(input.radii.tl, 0, maxRadius)
  const tr = clamp(input.radii.tr, 0, maxRadius)
  const br = clamp(input.radii.br, 0, maxRadius)
  const bl = clamp(input.radii.bl, 0, maxRadius)

  // Per-edge tail bump appended via this helper. `side` matches the active tail
  // edge; otherwise we just return empty and the outer walker continues straight.
  function tailSegment(side: TailSide): string {
    if (!tail.show || tail.side !== side) return ''

    const halfW = tail.width / 2
    const h = tail.height

    // Edge-local "start" / "end" along the corner-bounded usable area, then
    // lerp by offsetPercent to place the tail base center.
    const edgeBounds = (() => {
      if (side === 'top') return { start: left + tl, end: right - tr }
      if (side === 'bottom') return { start: left + bl, end: right - br }
      if (side === 'left') return { start: top + tl, end: bottom - bl }
      return { start: top + tr, end: bottom - br } // 'right'
    })()
    const usableLen = Math.max(0, edgeBounds.end - edgeBounds.start - tail.width)
    const baseCenter =
      edgeBounds.start + halfW + (usableLen * clamp(tail.offsetPercent, 0, 100)) / 100

    // tipRadius capped so the rounded apex can't consume the whole tail.
    const sideLen = Math.sqrt(halfW * halfW + h * h)
    const halfApexAngle = Math.atan2(halfW, h)
    const maxTangentDist = sideLen * 0.5
    const tangentDist = Math.min(
      tail.tipRadius / Math.max(Math.tan(halfApexAngle), 0.001),
      maxTangentDist,
    )
    const rounded = tail.tipRadius > 0 && tangentDist > 0

    if (side === 'top') {
      const baseStart = baseCenter - halfW
      const baseEnd = baseCenter + halfW
      const tipX = baseCenter
      const tipY = top - h
      if (!rounded) {
        return ` L${fmt(baseStart)} ${fmt(top)} L${fmt(tipX)} ${fmt(tipY)} L${fmt(baseEnd)} ${fmt(top)}`
      }
      const dx = (halfW / sideLen) * tangentDist
      const dy = (h / sideLen) * tangentDist
      const p1x = tipX - halfW + dx
      const p1y = tipY + dy
      const p2x = tipX + halfW - dx
      const p2y = tipY + dy
      const r = tangentDist * Math.tan(halfApexAngle)
      return ` L${fmt(baseStart)} ${fmt(top)} L${fmt(p1x)} ${fmt(p1y)} A${fmt(r)} ${fmt(r)} 0 0 1 ${fmt(p2x)} ${fmt(p2y)} L${fmt(baseEnd)} ${fmt(top)}`
    }
    if (side === 'right') {
      const baseStart = baseCenter - halfW
      const baseEnd = baseCenter + halfW
      const tipX = right + h
      const tipY = baseCenter
      if (!rounded) {
        return ` L${fmt(right)} ${fmt(baseStart)} L${fmt(tipX)} ${fmt(tipY)} L${fmt(right)} ${fmt(baseEnd)}`
      }
      const dx = (h / sideLen) * tangentDist
      const dy = (halfW / sideLen) * tangentDist
      const p1x = tipX - dx
      const p1y = tipY - halfW + dy
      const p2x = tipX - dx
      const p2y = tipY + halfW - dy
      const r = tangentDist * Math.tan(halfApexAngle)
      return ` L${fmt(right)} ${fmt(baseStart)} L${fmt(p1x)} ${fmt(p1y)} A${fmt(r)} ${fmt(r)} 0 0 1 ${fmt(p2x)} ${fmt(p2y)} L${fmt(right)} ${fmt(baseEnd)}`
    }
    if (side === 'bottom') {
      // Walking from right to left along the bottom edge.
      const baseStart = baseCenter + halfW
      const baseEnd = baseCenter - halfW
      const tipX = baseCenter
      const tipY = bottom + h
      if (!rounded) {
        return ` L${fmt(baseStart)} ${fmt(bottom)} L${fmt(tipX)} ${fmt(tipY)} L${fmt(baseEnd)} ${fmt(bottom)}`
      }
      const dx = (halfW / sideLen) * tangentDist
      const dy = (h / sideLen) * tangentDist
      const p1x = tipX + halfW - dx
      const p1y = tipY - dy
      const p2x = tipX - halfW + dx
      const p2y = tipY - dy
      const r = tangentDist * Math.tan(halfApexAngle)
      return ` L${fmt(baseStart)} ${fmt(bottom)} L${fmt(p1x)} ${fmt(p1y)} A${fmt(r)} ${fmt(r)} 0 0 1 ${fmt(p2x)} ${fmt(p2y)} L${fmt(baseEnd)} ${fmt(bottom)}`
    }
    // 'left' — walking from bottom to top along the left edge.
    const baseStart = baseCenter + halfW
    const baseEnd = baseCenter - halfW
    const tipX = left - h
    const tipY = baseCenter
    if (!rounded) {
      return ` L${fmt(left)} ${fmt(baseStart)} L${fmt(tipX)} ${fmt(tipY)} L${fmt(left)} ${fmt(baseEnd)}`
    }
    const dx = (h / sideLen) * tangentDist
    const dy = (halfW / sideLen) * tangentDist
    const p1x = tipX + dx
    const p1y = tipY + halfW - dy
    const p2x = tipX + dx
    const p2y = tipY - halfW + dy
    const r = tangentDist * Math.tan(halfApexAngle)
    return ` L${fmt(left)} ${fmt(baseStart)} L${fmt(p1x)} ${fmt(p1y)} A${fmt(r)} ${fmt(r)} 0 0 1 ${fmt(p2x)} ${fmt(p2y)} L${fmt(left)} ${fmt(baseEnd)}`
  }

  let d = `M${fmt(left + tl)} ${fmt(top)}`
  d += tailSegment('top')
  d += ` L${fmt(right - tr)} ${fmt(top)}`
  if (tr > 0) d += ` A${fmt(tr)} ${fmt(tr)} 0 0 1 ${fmt(right)} ${fmt(top + tr)}`
  d += tailSegment('right')
  d += ` L${fmt(right)} ${fmt(bottom - br)}`
  if (br > 0) d += ` A${fmt(br)} ${fmt(br)} 0 0 1 ${fmt(right - br)} ${fmt(bottom)}`
  d += tailSegment('bottom')
  d += ` L${fmt(left + bl)} ${fmt(bottom)}`
  if (bl > 0) d += ` A${fmt(bl)} ${fmt(bl)} 0 0 1 ${fmt(left)} ${fmt(bottom - bl)}`
  d += tailSegment('left')
  d += ` L${fmt(left)} ${fmt(top + tl)}`
  if (tl > 0) d += ` A${fmt(tl)} ${fmt(tl)} 0 0 1 ${fmt(left + tl)} ${fmt(top)}`
  d += ' Z'

  return d
}
